/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Eye,
  Home,
  Megaphone,
  Phone,
  ScrollText,
  Trash2,
} from "lucide-react";
import { notificationsApi } from "./api";
import "./NotificationsPage.css";

const PAGE_SIZE = 25;

const TYPE_ICON = {
  "phone_access.viewed": Eye,
  "phone_change.submitted": Phone,
  "phone_change.approved": Phone,
  "phone_change.rejected": Phone,
  "villa_membership.submitted": Home,
  "villa_membership.approved": Home,
  "villa_membership.rejected": Home,
  "document.added": ScrollText,
  "document.comment_added": ScrollText,
  "announcement.published": Megaphone,
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffS = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (diffS < 60) return `${diffS}s ago`;
  const m = Math.floor(diffS / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsPage() {
  const [tab, setTab] = useState("all"); // "all" | "unread"
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const sentinelRef = useRef(null);

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await notificationsApi.list({
        limit: PAGE_SIZE,
        unreadOnly: tab === "unread",
      });
      setItems(d.items || []);
      setCursor(d.next_cursor || null);
      setUnreadCount(d.unread_count ?? 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await notificationsApi.list({
        cursor,
        limit: PAGE_SIZE,
        unreadOnly: tab === "unread",
      });
      setItems((prev) => [...prev, ...(d.items || [])]);
      setCursor(d.next_cursor || null);
      setUnreadCount(d.unread_count ?? 0);
    } catch (_) {
      // Surface only first-load errors prominently; "load more" can stay silent.
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, tab]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  // Infinite scroll via IntersectionObserver.
  useEffect(() => {
    if (!cursor) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [cursor, loadMore]);

  const onRowClick = async (n) => {
    if (!n.read_at) {
      setItems((prev) =>
        prev.map((x) =>
          x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await notificationsApi.markRead(n.id);
      } catch (_) {}
    }
    if (n.link) navigate(n.link);
  };

  const onDelete = async (n, e) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.read_at) setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await notificationsApi.remove(n.id);
    } catch (_) {
      loadFirst();
    }
  };

  const markAll = async () => {
    setItems((prev) =>
      prev.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() }))
    );
    setUnreadCount(0);
    try {
      await notificationsApi.markAllRead();
    } catch (_) {
      loadFirst();
    }
  };

  return (
    <div className="vsnp-shell">
      <div className="vsnp-head">
        <div>
          <h1>Notifications</h1>
          <p className="vsnp-sub">
            {unreadCount > 0
              ? `${unreadCount} unread`
              : "You're all caught up."}
          </p>
        </div>
        <button
          type="button"
          className="vsnp-btn-secondary"
          onClick={markAll}
          disabled={unreadCount === 0}
        >
          Mark all read
        </button>
      </div>

      <div className="vsnp-tabs">
        <button
          className={tab === "all" ? "active" : ""}
          onClick={() => setTab("all")}
        >
          All
        </button>
        <button
          className={tab === "unread" ? "active" : ""}
          onClick={() => setTab("unread")}
        >
          Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
        </button>
      </div>

      {error && <div className="vsnp-error">{error}</div>}
      {loading && items.length === 0 && (
        <div className="vsnp-empty">Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="vsnp-empty">
          <Bell size={32} strokeWidth={1.4} />
          <p>
            {tab === "unread"
              ? "Nothing unread."
              : "No notifications yet."}
          </p>
        </div>
      )}

      <ul className="vsnp-list">
        {items.map((n) => {
          const Icon = TYPE_ICON[n.type] || Bell;
          const unread = !n.read_at;
          return (
            <li
              key={n.id}
              className={`vsnp-row ${unread ? "unread" : ""}`}
              onClick={() => onRowClick(n)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onRowClick(n);
              }}
            >
              <span className="vsnp-row-icon">
                <Icon size={16} strokeWidth={2.1} />
              </span>
              <div className="vsnp-row-body">
                <div className="vsnp-row-title">{n.title}</div>
                {n.body && <div className="vsnp-row-snippet">{n.body}</div>}
                <div className="vsnp-row-meta">{timeAgo(n.created_at)}</div>
              </div>
              <button
                type="button"
                className="vsnp-row-delete"
                onClick={(e) => onDelete(n, e)}
                aria-label="Delete notification"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </li>
          );
        })}
      </ul>

      {cursor && (
        <div ref={sentinelRef} className="vsnp-sentinel">
          {loadingMore ? "Loading more…" : ""}
        </div>
      )}
    </div>
  );
}
